/**
 * DomainCardsSelection Component
 *
 * Character Creation Step 8: Select 2 starting domain cards
 *
 * Reuses the existing DomainCardSelectorModal from LevelUpWizard
 * to maintain code consistency and avoid duplication
 */

import React, { useState, useEffect } from 'react';
import { CharacterCreationData } from './CharacterCreationWizard';
import { ContentReference, DomainCard } from '../../types/domain-card';
import { DomainCardSelectorModal } from '../Modals/DomainCardSelectorModal';
import { getCardsByIds } from '../../utils/domainCardLoader';
import { ContentText } from '../Common/ContentText';
import styles from './DomainCardsSelection.module.css';

interface DomainCardsSelectionProps {
  characterData: CharacterCreationData;
  onDataChange: (data: Partial<CharacterCreationData>) => void;
}

const DomainCardsSelection: React.FC<DomainCardsSelectionProps> = ({
  characterData,
  onDataChange
}) => {
  const [selectedCardIds, setSelectedCardIds] = useState<string[]>([]);
  const [selectedCards, setSelectedCards] = useState<DomainCard[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

  // Initialize selections from characterData if they exist
  useEffect(() => {
    if (characterData.domainCards && characterData.domainCards.length > 0) {
      const cardIds = characterData.domainCards.map(ref => ref.contentId);
      setSelectedCardIds(cardIds);
      loadCardData(cardIds);
    }
  }, []);

  /**
   * Load full card data for selected cards
   */
  const loadCardData = async (cardIds: string[]) => {
    const cards = await getCardsByIds(cardIds);
    setSelectedCards(cards);
  };

  /**
   * Handle card selection from modal
   * Called when user confirms selection of domain cards
   */
  const handleCardSelect = (cardIds: string[]) => {
    // Update selected card IDs
    setSelectedCardIds(cardIds);
    loadCardData(cardIds);

    // Update character data
    const contentRefs: ContentReference[] = cardIds.map(id => ({
      contentId: id,
      contentType: 'domain_card',
      quantity: 1
    }));

    onDataChange({ domainCards: contentRefs });

    // Close modal after confirming selection
    setModalOpen(false);
  };

  /**
   * Handle deselecting a single card from the display
   */
  const handleDeselectCard = (cardId: string) => {
    const newSelection = selectedCardIds.filter(id => id !== cardId);
    handleCardSelect(newSelection);
    setExpandedCardId(null);
  };

  /**
   * Clear all selections
   */
  const handleClearAll = () => {
    setSelectedCardIds([]);
    onDataChange({ domainCards: [] });
  };

  if (!characterData.class) {
    return (
      <div className={styles.domainCardsSelection}>
        <div className={styles.header}>
          <h2>Select Your Starting Domain Cards</h2>
        </div>
        <div className={styles.empty}>
          <p>Please select a class first.</p>
        </div>
      </div>
    );
  }

  const isComplete = selectedCardIds.length === 2;

  return (
    <div className={styles.domainCardsSelection}>
      {/* Header */}
      <div className={styles.header}>
        <h2>Select Your Starting Domain Cards</h2>
        <p>
          Choose 2 domain cards from your class's available domains.
          These cards represent your character's magical abilities and powers.
        </p>
      </div>

      {/* Selection Status */}
      <div className={styles.selectionStatus}>
        <div className={styles.statusText}>
          <strong>Selected:</strong> {selectedCardIds.length} / 2
        </div>
        {selectedCardIds.length > 0 && (
          <button
            onClick={handleClearAll}
            className={styles.clearButton}
            aria-label="Clear all selections"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Display Selected Cards */}
      {selectedCards.length > 0 && (
        <div className={styles.selectedCardsContainer}>
          {selectedCards.map((card) => (
            <div key={card.id} className={styles.selectedCardItem}>
              <div className={styles.selectedCardHeader}>
                <div className={styles.selectedCardInfo}>
                  <span className={styles.selectedCardLevel}>Lv {card.level}</span>
                  <span className={styles.selectedCardName}>{card.name}</span>
                  <span className={styles.selectedCardDomain}>{card.domain}</span>
                </div>
                <button
                  onClick={() => setExpandedCardId(expandedCardId === card.id ? null : card.id)}
                  className={styles.expandButton}
                  aria-label={`${expandedCardId === card.id ? 'Hide' : 'Show'} details for ${card.name}`}
                >
                  {expandedCardId === card.id ? '▼' : '▶'}
                </button>
                <button
                  onClick={() => handleDeselectCard(card.id)}
                  className={styles.deselectButton}
                  aria-label={`Remove ${card.name}`}
                  title="Remove this card"
                >
                  ✕
                </button>
              </div>

              {expandedCardId === card.id && (
                <div className={styles.selectedCardDetails}>
                  <div className={styles.cardDetailsMeta}>
                    <span className={styles.cardType}>{card.type}</span>
                    <span className={styles.recallCost}>Recall: {card.recallCost}</span>
                  </div>
                  <ContentText
                    content={card.description}
                    className={styles.cardDetailsDescription}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Selection Progress Message */}
      {!isComplete && (
        <div className={styles.progressMessage}>
          <p>ℹ️ {2 - selectedCardIds.length} more card{selectedCardIds.length === 1 ? '' : 's'} to select</p>
        </div>
      )}

      {isComplete && (
        <div className={styles.successMessage}>
          <p>✓ Domain cards selected! Ready to continue.</p>
        </div>
      )}

      {/* Open Modal Button */}
      <button
        onClick={() => setModalOpen(true)}
        className={styles.selectButton}
        aria-label="Open domain card selector"
      >
        {selectedCardIds.length === 0
          ? 'Select Domain Cards'
          : `Select Domain Cards (${selectedCardIds.length}/2)`}
      </button>

      {/* Domain Card Selector Modal - Shared component */}
      <DomainCardSelectorModal
        isOpen={modalOpen}
        characterClass={characterData.class}
        characterLevel={1} // Level 1 for character creation
        ownedCardIds={selectedCardIds}
        selectCount={2}
        onConfirm={handleCardSelect}
        onCancel={() => setModalOpen(false)}
        title="Select Your Starting Domain Cards"
        subtitle="Choose 2 domain cards from your class's available domains. These represent your character's magical abilities and powers."
      />
    </div>
  );
};

export default DomainCardsSelection;
